import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';
import Paper from '@material-ui/core/Paper';
import { Search } from 'components/Search/Search';
import { PageIndex } from 'components/UI/PageIndex';
import { TableLoader } from 'components/Loaders/TableLoader';
import { Row } from 'components/UI/Row';
import { axiosInstance } from '../../axiosInstance';

type Resource = {
  link_id: string;
  link_type: string;
  linked: boolean;
  primary_name: string;
  secondary_name: string;
};

type LinkedResource = {
  link_id: string;
  link_type: string;
};

type Pagination = {
  index: number;
  indexesCount?: number;
  searchTerm?: string;
  size?: number;
};

const Resources: FC<{
  checkedValues: any;
  linkType: string;
  searchLabel: string;
  setCheckedValues: any;
  title: string;
}> = ({ linkType, searchLabel, title, setCheckedValues, checkedValues }) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const titleRef = useRef<HTMLTableElement>(null);
  const { policyId } = useParams<{ policyId: string }>();
  const [resourcesList, setResourcesList] = useState<Resource[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<Pagination>({
    index: 1
  });

  /* const [checkedValues, setCheckedValues] = useState<LinkedResource[]>([]); */

  const handleSelect = (link_id: string, link_type: string) => {
    const idx = checkedValues?.findIndex((cv: LinkedResource) => cv.link_id === link_id && cv.link_type === link_type);

    let newNames = null;
    if (idx >= 0) {
      newNames = [...checkedValues];
      newNames.splice(idx, 1);
    } else {
      newNames = [...(checkedValues ?? []), { link_id, link_type }];
    }

    setCheckedValues(newNames);

    return newNames;
  };

  const scrollTopTable = () => {
    if (titleRef) {
      titleRef?.current?.scrollIntoView();
    }
  };

  // TODO: custom hook
  const fetchResources = useCallback(
    async (page: number, searchTerm?: string) => {
      setIsLoading(true);

      const { data } = await axiosInstance.get(`/acls/policies/${policyId}/resources/${linkType}`, {
        params: { index: page, size: 30, search: searchTerm }
      });
      const { resources, index, indexes_count: indexesCount, size } = data;
      setResourcesList(resources);
      setPagination({ index, indexesCount, size, searchTerm });
      setIsLoading(false);
    },
    [policyId, linkType]
  );

  useEffect(() => {
    fetchResources(1);
  }, []);

  return (
    <>
      <div style={{ margin: '50px 0 15px' }}>
        <h5 ref={titleRef}>{title}</h5>
      </div>
      <div style={{ marginBottom: '15px' }}>
        <Search
          resetFunction={() => {
            fetchResources(1);
            scrollTopTable();
          }}
          searchFunction={(st: string) => {
            fetchResources(1, st);
            scrollTopTable();
          }}
          searchLabel={searchLabel}
        />
      </div>
      <TableContainer component={Paper} elevation={3} style={{ maxHeight: '50vh', height: '100%' }}>
        <Table stickyHeader aria-label={title}>
          <TableHead>
            <TableRow>
              <TableCell>{t(`acl.policy.${linkType} primary name`)}</TableCell>
              <TableCell>{t(`acl.policy.${linkType} secondary name`)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && <TableLoader colSpan={2} />}
            {!isLoading &&
              resourcesList.map(({ link_id, link_type, primary_name: name, secondary_name: description }) => (
                <TableRow
                  key={link_id}
                  hover
                  onClick={() => {
                    setValue('resources', handleSelect(link_id, link_type));
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Controller
                      name="resources"
                      control={control}
                      render={({ onChange: onCheckChange }) => (
                        <Checkbox
                          checked={!isEmpty(checkedValues.find((cv: LinkedResource) => cv.link_id === link_id))}
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            e.stopPropagation();
                            onCheckChange(handleSelect(link_id, link_type));
                          }}
                        />
                      )}
                    />
                    {name}
                  </TableCell>
                  <TableCell>{description}</TableCell>
                </TableRow>
              ))}
            {isEmpty(resourcesList) && (
              <TableRow>
                <TableCell align="center">{t(`acl.${linkType} no data`)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PageIndex
        justifyContent="center"
        index={pagination.index}
        indexesCount={pagination.indexesCount || 0}
        margin="20px 0 0 0"
        onChange={(_, page: number) => {
          fetchResources(page, pagination.searchTerm);
          scrollTopTable();
        }}
      />
    </>
  );
};

export { Resources };
